import React, { useMemo } from 'react'
import WistiaPlayer from 'react-player/wistia'

export interface AppProps {
  aspectRatio: string | null
  wistiaShareUrl: string
}

type AspectRatio = [number, number]

const aspectRatioRegex = /^(\d+):(\d+)$/
const defaultAspectRatio: AspectRatio = [16, 9]

function isValidAspectRatioComponent(
  value: number | undefined
): value is number {
  return value != null && !Number.isNaN(value) && value > 0
}

function isValidAspectRatio(
  ratio: (number | undefined)[]
): ratio is AspectRatio {
  const [width, height] = ratio
  return (
    isValidAspectRatioComponent(width) && isValidAspectRatioComponent(height)
  )
}

const parseNumber = (value: string | undefined): number | undefined =>
  value ? Number(value) : undefined

export const App: React.VFC<AppProps> = ({ aspectRatio, wistiaShareUrl }) => {
  const [aspectRatioWidth, aspectRatioHeight] = useMemo(() => {
    if (!aspectRatio) {
      return defaultAspectRatio
    }
    const match = aspectRatio.match(aspectRatioRegex)
    const ratio = [parseNumber(match?.[1]), parseNumber(match?.[2])]
    if (!isValidAspectRatio(ratio)) {
      console.warn(
        `Invalid aspect ratio (${aspectRatio}). Using default ${defaultAspectRatio[0]}:${defaultAspectRatio[1]}`
      )
      return defaultAspectRatio
    }
    return ratio
  }, [aspectRatio])

  return (
    <div
      style={{
        position: 'relative',
        paddingTop: `${(100 * aspectRatioHeight) / aspectRatioWidth}%`,
      }}
    >
      <WistiaPlayer
        url={wistiaShareUrl}
        controls
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />
    </div>
  )
}
